import Component from '@glimmer/component';
import { registerDestructor } from '@ember/destroyable';
import { service } from '@ember/service';
import { action } from '@ember/object';
import onInsert from 'madow-tech/modifiers/on-insert';
import type MarsCanvasRegistryService from 'madow-tech/services/mars-canvas-registry';
import {
  initMarsScene,
  startMarsSceneAnimation,
  stopMarsSceneAnimation,
  type MarsSceneState,
  type QualityTier,
  QUALITY_CONFIGS,
} from 'madow-tech/lib/canvas/mars-scene';
import { initSkyStars } from 'madow-tech/lib/canvas/sky-stars';
import type DeviceDetectionService from 'madow-tech/services/device-detection';
import { spawnShootingStar, type ColorPalette } from 'madow-tech/lib/canvas/shooting-stars';

interface MarsCanvasSignature {
  Args?: {
    colorPalette?: ColorPalette;
  };
  Element: HTMLCanvasElement;
}

export default class MarsCanvas extends Component<MarsCanvasSignature> {
  @service declare deviceDetection: DeviceDetectionService;
  @service declare marsCanvasRegistry: MarsCanvasRegistryService;

  canvas: HTMLCanvasElement | null = null;
  sceneState: MarsSceneState | null = null;
  parallaxOffset: number = 0;
  lowFpsStartTime: number | null = null;
  readonly LOW_FPS_THRESHOLD = 30;
  readonly LOW_FPS_DURATION = 2000;
  ultraValidationStartTime: number | null = null;
  readonly ULTRA_VALIDATION_DURATION = 3000;
  readonly ULTRA_VALIDATION_FPS_THRESHOLD = 55;
  isUltraValidating: boolean = false;
  qualityCheckIntervalId: number | null = null;
  starCountDebounceTimer: number | null = null;
  motionPaused: boolean = false;
  rotationSpeedMultiplier: number = 1.0;
  performanceTarget: number = 60;

  constructor(owner: unknown, args: any) {
    super(owner, args);
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        this.marsCanvasRegistry.register(this);
      }, 0);
    }
  }

  setupCanvas = (element: HTMLCanvasElement) => {
    this.canvas = element;

    // Set canvas size - make it taller than viewport for parallax
    // Reduce canvas height on mobile for better performance
    // Cap DPR to prevent excessive canvas sizes at extreme zoom levels
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const isMobile = this.deviceDetection.isPhone;
    // Mobile: 1.5x viewport height, Desktop: 2.5x
    const canvasHeight = viewportHeight * (isMobile ? 1.5 : 2.5);

    element.width = Math.round(rect.width * dpr);
    element.height = Math.round(canvasHeight * dpr);

    // Detect quality tier
    let qualityTier = this.deviceDetection.detectQualityTier() as QualityTier;

    // Force low quality on mobile for better performance
    if (isMobile && qualityTier !== 'low') {
      qualityTier = 'low';
    }

    if (qualityTier === 'ultra') {
      this.isUltraValidating = true;
      this.ultraValidationStartTime = performance.now();
    }

    // Initialize Canvas 2D scene
    // Note: We intentionally don't use ctx.scale(dpr, dpr) here
    // All rendering is done in device pixel coordinates for consistency
    const ctx = element.getContext('2d');
    if (ctx) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
    this.sceneState = initMarsScene(element, qualityTier, this.args.colorPalette);

    // Direct scroll handler - no easing, stars scroll naturally with the page
    const handleScroll = () => {
      const scrollY = window.scrollY || 0;
      this.parallaxOffset = -scrollY * 0.3;

      if (this.sceneState) {
        (this.sceneState as any).parallaxOffset = this.parallaxOffset;
      }
    };

    handleScroll();

    if (this.sceneState) {
      startMarsSceneAnimation(element, this.sceneState);
    }

    // Setup throttled quality check
    this.qualityCheckIntervalId = window.setInterval(() => {
      this.checkAndAdjustQuality();
    }, 500);

    // Handle window resize
    const handleResize = () => {
      if (!this.canvas) return;

      // Cap DPR to prevent excessive canvas sizes at extreme zoom levels
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = this.canvas.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const isMobile = this.deviceDetection.isPhone;
      // Mobile: 1.5x viewport height, Desktop: 2.5x
      const canvasHeight = viewportHeight * (isMobile ? 1.5 : 2.5);

      this.canvas.width = Math.round(rect.width * dpr);
      this.canvas.height = Math.round(canvasHeight * dpr);

      const ctx = this.canvas.getContext('2d');
      if (ctx) {
        // Reset transform to identity matrix
        // Note: We don't use ctx.scale() - all rendering is in device pixel coordinates
        ctx.setTransform(1, 0, 0, 1, 0, 0);
      }

      if (this.sceneState) {
        stopMarsSceneAnimation(this.sceneState);
        const currentTier = this.sceneState.qualityTier || this.deviceDetection.detectQualityTier() as QualityTier;
        this.sceneState = initMarsScene(this.canvas, currentTier, this.args.colorPalette);
        handleScroll();
        startMarsSceneAnimation(this.canvas, this.sceneState);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });

    registerDestructor(this, () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      if (this.qualityCheckIntervalId !== null) {
        window.clearInterval(this.qualityCheckIntervalId);
      }
      if (this.starCountDebounceTimer !== null) {
        window.clearTimeout(this.starCountDebounceTimer);
      }
      if (this.sceneState) {
        stopMarsSceneAnimation(this.sceneState);
      }
      this.marsCanvasRegistry.unregister();
    });
  };

  checkAndAdjustQuality() {
    if (!this.sceneState?.performance) return;

    const currentFps = this.sceneState.performance.fps;
    const currentTier = this.sceneState.qualityTier;

    // Ultra tier validation
    if (this.isUltraValidating && this.ultraValidationStartTime) {
      const elapsed = performance.now() - this.ultraValidationStartTime;

      if (elapsed >= this.ULTRA_VALIDATION_DURATION) {
        this.isUltraValidating = false;
        this.ultraValidationStartTime = null;

        if (currentFps < this.ULTRA_VALIDATION_FPS_THRESHOLD) {
          this.applyQualityTier('high');
        }
      }
      return;
    }

    // Regular quality check
    if (currentFps < this.LOW_FPS_THRESHOLD) {
      if (this.lowFpsStartTime === null) {
        this.lowFpsStartTime = performance.now();
      } else if (performance.now() - this.lowFpsStartTime >= this.LOW_FPS_DURATION) {
        const tierOrder: QualityTier[] = ['ultra', 'high', 'medium', 'low'];
        const currentIndex = tierOrder.indexOf(currentTier);
        if (currentIndex < tierOrder.length - 1) {
          this.applyQualityTier(tierOrder[currentIndex + 1]);
        }
        this.lowFpsStartTime = null;
      }
    } else {
      this.lowFpsStartTime = null;
    }
  }

  applyQualityTier(newTier: QualityTier) {
    if (!this.canvas || !this.sceneState) return;

    const showPerfOverlay = (this.sceneState.performance as any)?.showOverlay;
    stopMarsSceneAnimation(this.sceneState);

    this.sceneState = initMarsScene(this.canvas, newTier, this.args.colorPalette);

    if (this.sceneState.performance && showPerfOverlay) {
      (this.sceneState.performance as any).showOverlay = showPerfOverlay;
    }

    startMarsSceneAnimation(this.canvas, this.sceneState);
  }

  // Debug panel control methods
  @action
  setStarCount(count: number) {
    if (this.starCountDebounceTimer !== null) {
      window.clearTimeout(this.starCountDebounceTimer);
    }

    this.starCountDebounceTimer = window.setTimeout(() => {
      this.starCountDebounceTimer = null;
      if (this.canvas && this.sceneState) {
        this.sceneState.skyStars = initSkyStars(this.canvas, count);
        this.sceneState.qualityConfig = {
          ...this.sceneState.qualityConfig,
          starCount: count,
        };
      }
    }, 150);
  }

  @action
  setQualityTier(tier: QualityTier) {
    if (!this.canvas || !this.sceneState) return;

    const newConfig = QUALITY_CONFIGS[tier];
    this.sceneState.qualityTier = tier;
    this.sceneState.qualityConfig = { ...newConfig };
    this.sceneState.skyStars = initSkyStars(this.canvas, newConfig.starCount);
    // Reinitialize shooting stars with current color palette
    if (this.sceneState.shootingStars && this.args.colorPalette) {
      this.sceneState.shootingStars.colorPalette = this.args.colorPalette;
    }
  }

  @action
  toggleShootingStars(enabled: boolean) {
    if (this.sceneState) {
      this.sceneState.qualityConfig.enableShootingStars = enabled;
    }
  }

  @action
  toggleBlurEffects(enabled: boolean) {
    if (this.sceneState) {
      this.sceneState.qualityConfig.enableBlurEffects = enabled;
    }
  }

  @action
  triggerShootingStar() {
    if (this.sceneState && this.canvas) {
      spawnShootingStar(this.sceneState.shootingStars, this.canvas);
    }
  }

  @action
  setRotationSpeed(multiplier: number) {
    this.rotationSpeedMultiplier = Math.max(0.1, Math.min(5, multiplier));
    if (this.sceneState) {
      const baseSpeed = 0.0003;
      this.sceneState.skyStars.rotationSpeed = this.motionPaused ? 0 : baseSpeed * this.rotationSpeedMultiplier;
    }
  }

  @action
  toggleMotion(enabled: boolean) {
    this.motionPaused = !enabled;
    if (this.sceneState) {
      const baseSpeed = 0.0003;
      this.sceneState.skyStars.rotationSpeed = this.motionPaused ? 0 : baseSpeed * this.rotationSpeedMultiplier;
    }
  }

  get isMotionEnabled(): boolean {
    return !this.motionPaused;
  }

  get currentRotationSpeed(): number {
    return this.rotationSpeedMultiplier;
  }

  get currentFPS(): number {
    return this.sceneState?.performance?.fps || 0;
  }

  get currentFrameTime(): number {
    return this.sceneState?.performance?.frameTime || 0;
  }

  get averageFrameTime(): number {
    return this.sceneState?.performance?.avgFrameTime || 0;
  }

  get currentStarCount(): number {
    return this.sceneState?.skyStars.stars.length || 0;
  }

  get maxStarCount(): number {
    return this.sceneState?.qualityConfig.starCount || 0;
  }

  get currentQualityTier(): QualityTier {
    return this.sceneState?.qualityTier || 'high';
  }

  get renderedStarCount(): number {
    return this.sceneState?.skyStars.renderedCount || 0;
  }

  get culledStarCount(): number {
    return this.sceneState?.skyStars.culledCount || 0;
  }

  @action
  setPerformanceTarget(fps: number) {
    this.performanceTarget = Math.max(30, Math.min(120, fps));
  }

  <template>
    <canvas
      {{onInsert this.setupCanvas}}
      class="mars-canvas fixed inset-0 w-full -z-10"
      ...attributes
    ></canvas>
  </template>
}
